import React, { useEffect, useState } from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';

import apiOffers from 'api/offers';
import IRestorablePosition from 'api/interfaces/offers/IRestorablePosition';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';

const RestorePositionModal = ({
  isOpen,
  offerId,
  onRestored,
  toggle,
  type,
}: {
  isOpen: boolean;
  offerId: number;
  onRestored: () => void;
  toggle: () => void;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);

  const [positions, setPositions] = useState<IRestorablePosition[] | null>(
    null
  );
  const [restoringId, setRestoringId] = useState<number | null>(null);

  const isAdditionalOffer = type === ADDITIONAL_OFFER;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPositions(null);

    const getPositions = async () => {
      try {
        setPositions(
          isAdditionalOffer
            ? await apiOffers.additionalOffers.restorablePositions.get(offerId)
            : await apiOffers.restorablePositions.get(offerId)
        );
      } catch (error) {
        setPositions([]);
      }
    };

    getPositions();
  }, [isOpen, offerId, isAdditionalOffer]);

  const restore = async (position: IRestorablePosition) => {
    if (restoringId !== null) {
      return;
    }

    setRestoringId(position.offerProductId);

    try {
      if (isAdditionalOffer) {
        await apiOffers.additionalOffers.restorablePositions.restore(
          offerId,
          position.offerProductId
        );
      } else {
        await apiOffers.restorablePositions.restore(
          offerId,
          position.offerProductId
        );
      }

      setPositions(
        (positions ?? []).filter(
          (existing) => existing.offerProductId !== position.offerProductId
        )
      );
      dispatch(addMessage(t(`${translations.offers}:Position restored`)));
      onRestored();
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Position restore failed`)));
    } finally {
      setRestoringId(null);
    }
  };

  const getContent = () => {
    if (positions === null) {
      return <p>{t(`${translations.offers}:Loading positions`)}</p>;
    }

    if (positions.length === 0) {
      return <p>{t(`${translations.offers}:No positions to restore`)}</p>;
    }

    return (
      <Table size="sm" hover>
        <thead>
          <tr>
            <th>{t(`${translations.products}:Position`)}</th>
            <th>{t(`${translations.products}:Product`)}</th>
            <th>{t(`${translations.common}:Quantity`)}</th>
            <th>{t(`${translations.offers}:Revision`)}</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {positions.map((position) => (
            <tr key={position.offerProductId}>
              <td>{position.position}</td>
              <td>{position.name}</td>
              <td>
                {position.quantity === null
                  ? ''
                  : `${formatGroupedNumber(position.quantity, 2, 0)} ${t(
                      `${translations.common}:units:${position.unit}`
                    )}`}
              </td>
              <td>{position.revisionNumber}</td>
              <td>
                <Button
                  className="modal-button"
                  disabled={restoringId !== null}
                  onClick={() => restore(position)}
                  size="sm"
                >
                  {t(`${translations.offers}:Restore`)}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader className="modal-header" toggle={toggle}>
        {t(`${translations.offers}:Restore position from earlier revision`)}
      </ModalHeader>
      <ModalBody>{getContent()}</ModalBody>
      <ModalFooter>
        <Button onClick={toggle} className="modal-button">
          {t(`${translations.offers}:Close`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RestorePositionModal;
